import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Sheet from './Sheet';
import Icon from './Icon';
import { useApp, useAppPath } from '../lib/AppContext';
import { APP_VERSION } from '../lib/announcements';

// Announcements of new features, set up by admins in /admin/announcements. The server says which ones this
// user still has to see; this decides when: at most one tooltip, banner or "Có gì mới" sheet a session,
// only once the screen has been still for a moment with no sheet open and the button in view. "Mới" labels
// are quiet, so they don't count. Seen is seen: "Đã hiểu", tapping the feature, tapping elsewhere or ✕.
const AnnouncementsContext = createContext(null);
const useAnnouncements = () => useContext(AnnouncementsContext);

const INTERRUPTING = ['tooltip', 'banner', 'whats_new'];
// Still for this long before showing (also lets the trial offer, 1.2 s after opening, go first).
const STILL_MS = 1500;
// Seen marks not yet confirmed by the server (offline): kept on the phone and sent again next time.
const PENDING_KEY = 'macro-mate:announcements:pending';

// Per session (an app launch or page load), not per screen: survives moving between pages.
const session = { shown: null, usedAnchors: new Set(), dotsShown: new Set() };

// A built-in card (e.g. Nấu chung's banner, `cook_together_v1`) takes this session's one slot. True when it
// may show: the slot was free, or it is already this card's.
export function claimAnnouncementSlot(key) {
  if (session.shown && session.shown !== key) return false;
  session.shown = key;
  return true;
}

const readPending = () => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY)) || {};
  } catch {
    return {};
  }
};
const writePending = (pending) => {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Private mode: the server copy is enough.
  }
};

// In the viewport and not covered (by the tab bar, a sheet…): what's at its centre is the element itself.
const inView = (el) => {
  if (!el?.isConnected) return false;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.top < 0 || r.left < 0 || r.bottom > window.innerHeight || r.right > window.innerWidth) return false;
  const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
  return !!hit && el.contains(hit);
};
const dialogOpen = () => !!document.querySelector('dialog[open]');

export function AnnouncementsProvider({ children }) {
  const { api } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [list, setList] = useState([]);
  const [currentKey, setCurrentKey] = useState(null);
  const [hidden, setHidden] = useState(false);
  const anchors = useRef(new Map()); // anchor id -> { el, cleanup }
  const screens = useRef(new Map()); // screen id -> mounted banner slots
  const lastChange = useRef(Date.now());

  const sendSeen = useCallback((key, via) => {
    writePending({ ...readPending(), [key]: via });
    api.post(`/announcements/${encodeURIComponent(key)}/seen`, { via }).then(() => {
      const { [key]: _done, ...rest } = readPending();
      writePending(rest);
    }).catch(() => {});
  }, [api]);

  const load = useCallback(() => {
    const query = APP_VERSION ? `?${new URLSearchParams({ app_version: APP_VERSION })}` : '';
    api.get(`/announcements${query}`).then(({ announcements }) => {
      const pending = readPending();
      Object.entries(pending).forEach(([key, via]) => sendSeen(key, via));
      setList(announcements.filter((a) => !pending[a.key]));
    }).catch(() => {});
  }, [api, sendSeen]);
  useEffect(load, [load]);

  const seen = useCallback((key, via) => {
    setList((all) => all.filter((a) => a.key !== key));
    setCurrentKey((k) => (k === key ? null : k));
    sendSeen(key, via);
  }, [sendSeen]);

  // Tapping a button that has announcements is using the feature: theirs are done (the server tells "feature"
  // from "found it first" and times the first use, also for ones seen earlier). Once per button a session.
  const listRef = useRef(list);
  listRef.current = list;
  const used = useCallback((anchor) => {
    const done = listRef.current.filter((a) => (a.kind === 'tooltip' || a.kind === 'dot') && a.placement === anchor);
    if (done.length) {
      setList((all) => all.filter((a) => !done.includes(a)));
      setCurrentKey((k) => (done.some((a) => a.key === k) ? null : k));
    }
    if (session.usedAnchors.has(anchor)) return;
    session.usedAnchors.add(anchor);
    api.post('/announcements/used', { anchor }).catch(() => session.usedAnchors.delete(anchor));
  }, [api]);

  const register = useCallback((id, el) => {
    anchors.current.get(id)?.cleanup();
    anchors.current.delete(id);
    lastChange.current = Date.now();
    if (!el) return;
    // After the tap has done its job: marking it used removes the "Mới" label at once, and when the tap
    // landed on the label, React would then drop the click (its target is gone) and the button do nothing.
    const onClick = () => setTimeout(() => used(id));
    el.addEventListener('click', onClick);
    anchors.current.set(id, { el, cleanup: () => el.removeEventListener('click', onClick) });
  }, [used]);

  const registerScreen = useCallback((id) => {
    screens.current.set(id, (screens.current.get(id) || 0) + 1);
    lastChange.current = Date.now();
    return () => screens.current.set(id, screens.current.get(id) - 1);
  }, []);

  const dotShown = useCallback((key) => {
    if (session.dotsShown.has(key)) return;
    session.dotsShown.add(key);
    api.post(`/announcements/${encodeURIComponent(key)}/shown`).catch(() => {});
  }, [api]);

  // Moving, scrolling or a page changing restarts the "still" clock.
  useEffect(() => { lastChange.current = Date.now(); }, [pathname]);
  useEffect(() => {
    const moved = () => { lastChange.current = Date.now(); };
    window.addEventListener('scroll', moved, { capture: true, passive: true });
    window.addEventListener('resize', moved);
    return () => {
      window.removeEventListener('scroll', moved, { capture: true });
      window.removeEventListener('resize', moved);
    };
  }, []);

  const current = list.find((a) => a.key === currentKey) || null;
  const waiting = list.some((a) => INTERRUPTING.includes(a.kind));

  useEffect(() => {
    if (!waiting && !current) return undefined;
    const placeable = (a) => {
      if (a.kind === 'tooltip') return inView(anchors.current.get(a.placement)?.el);
      if (a.kind === 'banner') return screens.current.get(a.placement) > 0;
      return screens.current.get('home') > 0; // whats_new
    };
    const tick = () => {
      if (current) {
        // A tooltip steps aside while a sheet is open or its button isn't on screen, and comes back after.
        if (current.kind === 'tooltip') setHidden(dialogOpen() || !anchors.current.get(current.placement)?.el.isConnected);
        return;
      }
      if (session.shown || dialogOpen() || Date.now() - lastChange.current < STILL_MS) return;
      const next = list.find((a) => INTERRUPTING.includes(a.kind) && placeable(a));
      if (!next) return;
      session.shown = next.key;
      setHidden(false);
      setCurrentKey(next.key);
      api.post(`/announcements/${encodeURIComponent(next.key)}/shown`).catch(() => {});
    };
    const timer = setInterval(tick, 300);
    return () => clearInterval(timer);
  }, [api, list, current, waiting]);

  const value = useMemo(() => ({
    list, current, register, registerScreen, dotShown, seen,
  }), [list, current, register, registerScreen, dotShown, seen]);

  const anchorEl = current?.kind === 'tooltip' ? anchors.current.get(current.placement)?.el : null;
  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
      {anchorEl && !hidden && (
        <Coachmark key={current.key} announcement={current} anchor={anchorEl} onSeen={(via) => seen(current.key, via)} />
      )}
      <div data-theme="macro">
        <Sheet open={current?.kind === 'whats_new'} onClose={() => seen(current.key, 'close')} title={current?.title || 'Có gì mới'}>
          {current?.kind === 'whats_new' && (
            <WhatsNew announcement={current}
              onTry={(link) => { seen(current.key, 'cta'); navigate(path(link)); }}
              onDone={() => seen(current.key, 'got_it')} />
          )}
        </Sheet>
      </div>
    </AnnouncementsContext.Provider>
  );
}

// ---------- Tooltip (coachmark) ----------

// The dark card itself; also the admin panel's preview.
export function AnnouncementCard({ title, body, buttonLabel, onDone, buttonRef, ids = {} }) {
  return (
    <div className="rounded-[18px] bg-[#17201b] p-4 text-white shadow-xl">
      <p id={ids.title} className="text-[15px] font-bold">{title || 'Tiêu đề'}</p>
      {body && <p id={ids.body} className="mt-1 text-sm leading-snug text-white/75">{body}</p>}
      <div className="mt-3 flex justify-end">
        <button ref={buttonRef} type="button" onClick={onDone}
          className="btn btn-sm h-9 rounded-[10px] border-0 bg-white px-4 font-bold text-[#17201b] hover:bg-white/90">
          {buttonLabel || 'Đã hiểu'}
        </button>
      </div>
    </div>
  );
}

// Points at its button: below it, or above when there's no room; follows it on scroll. The button gets a
// soft ring. "Đã hiểu" takes focus so a screen reader reads the card at once.
function Coachmark({ announcement, anchor, onSeen }) {
  const cardRef = useRef(null);
  const buttonRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const width = Math.min(320, vw - 32);
      const height = cardRef.current?.offsetHeight || 140;
      const centre = r.left + r.width / 2;
      const left = Math.min(Math.max(centre - width / 2, 16), vw - 16 - width);
      const below = r.bottom + 12 + height <= window.innerHeight - 16 || r.top - 12 - height < 16;
      setPos({
        left, width, below, top: below ? r.bottom + 12 : r.top - 12 - height,
        arrow: Math.min(Math.max(centre - left, 24), width - 24),
        ring: { left: r.left - 4, top: r.top - 4, width: r.width + 8, height: r.height + 8, radius: getComputedStyle(anchor).borderRadius },
      });
    };
    place();
    window.addEventListener('scroll', place, { capture: true, passive: true });
    window.addEventListener('resize', place);
    const observer = new ResizeObserver(place);
    observer.observe(cardRef.current);
    return () => {
      window.removeEventListener('scroll', place, { capture: true });
      window.removeEventListener('resize', place);
      observer.disconnect();
    };
  }, [anchor]);

  // Once placed: a hidden element can't take focus.
  const placed = !!pos;
  useEffect(() => { if (placed) buttonRef.current?.focus({ preventScroll: true }); }, [placed]);

  // A tap anywhere else counts as seen (a tap on the button itself is "feature", handled by the anchor).
  useEffect(() => {
    const outside = (e) => {
      if (cardRef.current?.contains(e.target) || anchor.contains(e.target)) return;
      onSeen('outside');
    };
    const escape = (e) => e.key === 'Escape' && onSeen('close');
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [anchor, onSeen]);

  const ids = { title: `announce-${announcement.key}-title`, body: `announce-${announcement.key}-body` };
  return (
    <>
      {pos && (
        <div aria-hidden="true" className="pointer-events-none fixed z-[60] ring-4 ring-primary/30"
          style={{ left: pos.ring.left, top: pos.ring.top, width: pos.ring.width, height: pos.ring.height, borderRadius: pos.ring.radius }} />
      )}
      <div ref={cardRef} role="dialog" aria-modal="false" aria-labelledby={ids.title} aria-describedby={announcement.body ? ids.body : undefined}
        className="announce-in fixed z-[60]"
        style={pos ? { left: pos.left, top: pos.top, width: pos.width } : { left: 16, top: 0, width: 320, visibility: 'hidden' }}>
        {pos && (
          <span aria-hidden="true" className={`absolute size-3.5 rotate-45 rounded-[3px] bg-[#17201b] ${pos.below ? '-top-1.5' : '-bottom-1.5'}`}
            style={{ left: pos.arrow - 7 }} />
        )}
        <AnnouncementCard title={announcement.title} body={announcement.body} buttonLabel={announcement.button_label}
          buttonRef={buttonRef} ids={ids} onDone={() => onSeen('got_it')} />
      </div>
    </>
  );
}

// Put on the button or row an announcement can point at (a tooltip, a "Mới" label): `ref={useAnnouncementAnchor('plan_create')}`.
// The ids are listed in lib/announcements.js for the admin panel.
export function useAnnouncementAnchor(id) {
  const register = useAnnouncements()?.register;
  return useCallback((el) => register?.(id, el), [register, id]);
}

// ---------- "Mới" label ----------

// Inside an anchored element. `dot`: a small dot for round buttons, else a "Mới" pill.
export function NewBadge({ anchor, dot = false, className = '' }) {
  const ctx = useAnnouncements();
  const item = ctx?.list.find((a) => a.kind === 'dot' && a.placement === anchor);
  const dotShown = ctx?.dotShown;
  useEffect(() => { if (item) dotShown(item.key); }, [item, dotShown]);
  if (!item) return null;
  const about = item.title.replace(/^Mới:\s*/, '');
  if (dot) {
    return (
      <span className={`absolute -right-0.5 -top-0.5 size-3 rounded-full bg-[#eb6834] ring-2 ring-base-100 ${className}`}>
        <span className="sr-only">Mới: {about}</span>
      </span>
    );
  }
  return (
    <span className={`rounded-full bg-[#eb6834] px-2 py-0.5 text-[11px] font-bold leading-4 text-white ${className}`}>
      Mới<span className="sr-only">: {about}</span>
    </span>
  );
}

// ---------- Banner ----------

// A slot at the top of a screen; shows that screen's banner when it's this session's announcement.
export function AnnouncementBanner({ screen }) {
  const ctx = useAnnouncements();
  const path = useAppPath();
  const navigate = useNavigate();
  const registerScreen = ctx?.registerScreen;
  useEffect(() => registerScreen?.(screen), [registerScreen, screen]);
  const item = ctx?.current?.kind === 'banner' && ctx.current.placement === screen ? ctx.current : null;
  if (!item) return null;
  return (
    <section aria-label="Tính năng mới" className="announce-in flex items-start gap-3 rounded-[20px] bg-[#e3f1e9] p-4 text-[#17201b]">
      <span className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-white text-[#17804f]">
        <Icon name="sparkles" className="size-5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        <p className="font-bold">{item.title}</p>
        {item.body && <p className="text-sm leading-snug text-[#3d4a43]">{item.body}</p>}
        {item.link_path && (
          <button type="button" className="btn btn-sm mt-1 h-9 rounded-[10px] border-0 bg-[#17804f] px-4 font-bold text-white"
            onClick={() => { ctx.seen(item.key, 'cta'); navigate(path(item.link_path)); }}>
            {item.button_label || 'Thử ngay'}
          </button>
        )}
      </div>
      <button type="button" aria-label="Đóng" className="grid size-9 shrink-0 place-items-center rounded-full text-[#3d4a43]"
        onClick={() => ctx.seen(item.key, 'close')}>
        <Icon name="x" className="size-4" />
      </button>
    </section>
  );
}

// ---------- "Có gì mới" ----------

export function WhatsNew({ announcement, onTry, onDone }) {
  return (
    <div className="flex flex-col gap-4">
      {announcement.body && <p className="text-[15px] text-muted">{announcement.body}</p>}
      <ul className="flex flex-col gap-3">
        {announcement.items.map((item, i) => (
          <li key={item.title || i} className="flex items-start gap-3 rounded-[16px] bg-base-200 p-4">
            <span className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-base-100 text-brand-text">
              <Icon name="sparkles" className="size-5" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
              <p className="font-bold">{item.title}</p>
              {item.body && <p className="text-sm leading-snug text-muted">{item.body}</p>}
              {item.link_path && (
                <button type="button" className="btn btn-sm mt-1 h-9 rounded-[10px] border-2 border-primary bg-base-100 px-3 font-bold text-brand-text"
                  onClick={() => onTry(item.link_path)}>
                  Thử ngay
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <button type="button" className="btn btn-primary h-12 rounded-[14px] border-0 font-bold" onClick={onDone}>
        {announcement.button_label || 'Đã hiểu'}
      </button>
    </div>
  );
}
